import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/Edit';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { COLORS, Field, StatusChip } from '../argocd/ui';
import type { OpenBaoApi } from './api';
import type { BaoMount, BaoSecret } from './types';

function SecretEditor({
  open,
  title,
  initialName,
  nameEditable,
  initialData,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  initialName: string;
  nameEditable: boolean;
  initialData: Record<string, string>;
  busy: boolean;
  onClose: () => void;
  onSubmit: (name: string, data: Record<string, string>) => void;
}) {
  const [name, setName] = useState(initialName);
  const [rows, setRows] = useState<Array<{ key: string; value: string }>>([]);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    const entries = Object.entries(initialData);
    setRows(
      entries.length
        ? entries.map(([key, value]) => ({ key, value }))
        : [{ key: '', value: '' }],
    );
    setJsonMode(false);
    setJsonText(JSON.stringify(initialData, null, 2));
    setJsonError(null);
  }, [open, initialName, initialData]);

  const collect = (): Record<string, string> | null => {
    if (jsonMode) {
      try {
        const parsed = JSON.parse(jsonText);
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setJsonError('JSON must be an object');
          return null;
        }
        return Object.fromEntries(
          Object.entries(parsed).map(([key, value]) => [
            key,
            typeof value === 'string' ? value : JSON.stringify(value),
          ]),
        );
      } catch (parseError) {
        setJsonError(
          parseError instanceof Error ? parseError.message : 'Invalid JSON',
        );
        return null;
      }
    }
    return Object.fromEntries(
      rows
        .filter(row => row.key.trim())
        .map(row => [row.key.trim(), row.value]),
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          label="Path"
          value={name}
          disabled={!nameEditable}
          onChange={event => setName(event.target.value)}
          sx={{ mt: 0.5, mb: 1.5 }}
        />
        <Stack direction="row" sx={{ mb: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            sx={{ fontSize: 11, textTransform: 'none' }}
            onClick={() => {
              if (!jsonMode) {
                setJsonText(
                  JSON.stringify(
                    Object.fromEntries(
                      rows
                        .filter(row => row.key.trim())
                        .map(row => [row.key.trim(), row.value]),
                    ),
                    null,
                    2,
                  ),
                );
              } else {
                const data = collect();
                if (data === null) return;
                setRows(
                  Object.entries(data).map(([key, value]) => ({ key, value })),
                );
              }
              setJsonError(null);
              setJsonMode(current => !current);
            }}
          >
            {jsonMode ? 'Key/value editor' : 'JSON editor'}
          </Button>
        </Stack>
        {jsonMode ? (
          <TextField
            fullWidth
            multiline
            minRows={10}
            value={jsonText}
            onChange={event => setJsonText(event.target.value)}
            error={Boolean(jsonError)}
            helperText={jsonError}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        ) : (
          <Stack spacing={1}>
            {rows.map((row, index) => (
              <Stack key={index} direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="key"
                  value={row.key}
                  onChange={event =>
                    setRows(current =>
                      current.map((entry, entryIndex) =>
                        entryIndex === index
                          ? { ...entry, key: event.target.value }
                          : entry,
                      ),
                    )
                  }
                  sx={{ width: 220 }}
                />
                <TextField
                  size="small"
                  placeholder="value"
                  value={row.value}
                  onChange={event =>
                    setRows(current =>
                      current.map((entry, entryIndex) =>
                        entryIndex === index
                          ? { ...entry, value: event.target.value }
                          : entry,
                      ),
                    )
                  }
                  sx={{ flex: 1 }}
                  InputProps={{
                    sx: { fontFamily: 'monospace', fontSize: 12 },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() =>
                    setRows(current =>
                      current.filter((_entry, entryIndex) => entryIndex !== index),
                    )
                  }
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setRows(current => [...current, { key: '', value: '' }])
              }
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              Add row
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          onClick={() => {
            const data = collect();
            if (data !== null) onSubmit(name.trim(), data);
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Everything about a secrets view that belongs in a link. */
export interface SecretsLocation {
  /** KV mount path, without a trailing slash. */
  mount: string;
  /** Browse prefix inside the mount; empty is the mount root. */
  path: string;
  /** Full path of the open secret; empty when none is open. */
  secret: string;
  /** KV v2 version; 0 is the current one. */
  version: number;
}

export function SecretsView({
  api,
  namespace,
  onNotify,
  location,
  onNavigate,
}: {
  api: OpenBaoApi;
  namespace: string;
  onNotify: (severity: 'success' | 'error', message: string) => void;
  /** Mount, browse path, open secret and version — owned by the page so they live in the URL. */
  location: SecretsLocation;
  onNavigate: (next: Partial<SecretsLocation>) => void;
}) {
  const [mounts, setMounts] = useState<BaoMount[] | null>(null);
  const [mountsError, setMountsError] = useState<string | null>(null);
  const [entries, setEntries] = useState<string[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [secret, setSecret] = useState<BaoSecret | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [secretLoading, setSecretLoading] = useState(false);
  const [secretError, setSecretError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteAnchor, setDeleteAnchor] = useState<HTMLElement | null>(null);
  const [confirmDestroy, setConfirmDestroy] = useState<
    { kind: 'destroy'; version: number } | { kind: 'metadata' } | null
  >(null);

  const kvMounts = useMemo(
    () =>
      (mounts ?? []).filter(
        entry => entry.type === 'kv' && entry.kvVersion === '2',
      ),
    [mounts],
  );

  const path = location.path;
  const secretPath = location.secret || null;
  // Mount paths come back from OpenBao with a trailing slash; the URL carries
  // them without one, so compare on the trimmed form.
  const mount = useMemo(
    () =>
      (mounts ?? []).find(
        entry => entry.path.replace(/\/$/, '') === location.mount,
      ) ?? null,
    [mounts, location.mount],
  );

  useEffect(() => {
    setSecret(null);
    setMounts(null);
    setMountsError(null);
    api
      .mounts(namespace)
      .then(loaded => setMounts(loaded))
      .catch(error =>
        setMountsError(error instanceof Error ? error.message : String(error)),
      );
  }, [api, namespace]);

  useEffect(() => {
    if (!location.mount && kvMounts.length) {
      onNavigate({ mount: kvMounts[0].path.replace(/\/$/, '') });
    }
  }, [kvMounts, location.mount, onNavigate]);

  const loadList = useCallback(async () => {
    if (!mount) return;
    setListLoading(true);
    try {
      setEntries(await api.list(namespace, mount.path, path));
    } catch (error) {
      onNotify(
        'error',
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setListLoading(false);
    }
  }, [api, mount, namespace, onNotify, path]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openSecret = useCallback(
    (fullPath: string, version = 0) => onNavigate({ secret: fullPath, version }),
    [onNavigate],
  );

  // The open secret is whatever the address names — clicking a row and
  // following a link are the same code path.
  useEffect(() => {
    if (!mount || !secretPath) {
      setSecret(null);
      setSecretError(null);
      return undefined;
    }
    let cancelled = false;
    setSecretLoading(true);
    setSecretError(null);
    // Reveal state is per-session and never in the URL; reset it whenever a
    // different secret or version is shown.
    setRevealed(new Set());
    api
      .secret(namespace, mount.path, secretPath, location.version || undefined)
      .then(loaded => {
        if (!cancelled) setSecret(loaded);
      })
      .catch(error => {
        if (cancelled) return;
        setSecret(null);
        setSecretError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (!cancelled) setSecretLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, mount, namespace, secretPath, location.version, reloadTick]);

  const reloadSecret = useCallback(() => setReloadTick(tick => tick + 1), []);

  const closeSecret = useCallback(() => {
    onNavigate({ secret: '', version: 0 });
    setDeleteAnchor(null);
  }, [onNavigate]);

  const saveSecret = async (name: string, data: Record<string, string>) => {
    if (!mount) return;
    const fullPath = creating ? `${path}${name}` : name;
    setBusy(true);
    try {
      await api.writeSecret(namespace, mount.path, fullPath, data);
      onNotify('success', `Secret ${fullPath} written`);
      setEditorOpen(false);
      await loadList();
      if (!creating && secretPath) reloadSecret();
    } catch (error) {
      onNotify(
        'error',
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setBusy(false);
    }
  };

  const runVersionOperation = async (
    operation: 'delete' | 'undelete' | 'destroy',
    versions?: number[],
  ) => {
    if (!mount || !secretPath) return;
    setBusy(true);
    setDeleteAnchor(null);
    setConfirmDestroy(null);
    try {
      await api.versionOperation(
        namespace,
        operation,
        mount.path,
        secretPath,
        versions,
      );
      onNotify('success', `Secret ${operation} completed`);
      reloadSecret();
    } catch (error) {
      onNotify(
        'error',
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteAllVersions = async () => {
    if (!mount || !secretPath) return;
    setBusy(true);
    setConfirmDestroy(null);
    setDeleteAnchor(null);
    try {
      await api.deleteMetadata(namespace, mount.path, secretPath);
      onNotify('success', `Secret ${secretPath} permanently deleted`);
      closeSecret();
      await loadList();
    } catch (error) {
      onNotify(
        'error',
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setBusy(false);
    }
  };

  const crumbs = path.split('/').filter(Boolean);
  const folders = entries.filter(entry => entry.endsWith('/'));
  const leaves = entries.filter(entry => !entry.endsWith('/'));

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ width: 230, flexShrink: 0 }}>
        <Typography
          sx={{ mb: 1, color: COLORS.muted, fontSize: 11, fontWeight: 700 }}
        >
          SECRETS ENGINES
        </Typography>
        {mountsError && <Alert severity="error">{mountsError}</Alert>}
        {!mounts && !mountsError && <CircularProgress size={20} />}
        {(mounts ?? []).map(entry => {
          const enabled = entry.type === 'kv' && entry.kvVersion === '2';
          const selected = mount?.path === entry.path;
          return (
            <Box
              key={entry.path}
              onClick={() => {
                if (!enabled) return;
                onNavigate({
                  mount: entry.path.replace(/\/$/, ''),
                  path: '',
                  secret: '',
                  version: 0,
                });
              }}
              sx={{
                mb: 0.75,
                p: 1.25,
                border: `1px solid ${
                  selected ? COLORS.blue : COLORS.border
                }`,
                borderRadius: 2,
                bgcolor: COLORS.surface,
                cursor: enabled ? 'pointer' : 'default',
                opacity: enabled ? 1 : 0.55,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {entry.path}
              </Typography>
              <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                {entry.type}
                {entry.kvVersion ? ` v${entry.kvVersion}` : ''}
                {entry.description ? ` · ${entry.description}` : ''}
              </Typography>
            </Box>
          );
        })}
        {mounts && kvMounts.length === 0 && (
          <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
            No KV v2 engines visible to your token
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {mount && (
          <>
            <Stack
              direction="row"
              sx={{ mb: 1.5, alignItems: 'center', gap: 1 }}
            >
              <Breadcrumbs sx={{ flex: 1, fontSize: 13 }}>
                <Link
                  component="button"
                  underline="hover"
                  sx={{ color: COLORS.blue, fontSize: 13 }}
                  onClick={() => onNavigate({ path: '' })}
                >
                  {mount.path}
                </Link>
                {crumbs.map((crumb, index) => (
                  <Link
                    key={index}
                    component="button"
                    underline="hover"
                    sx={{ color: COLORS.blue, fontSize: 13 }}
                    onClick={() =>
                      onNavigate({
                        path: `${crumbs.slice(0, index + 1).join('/')}/`,
                      })
                    }
                  >
                    {crumb}
                  </Link>
                ))}
              </Breadcrumbs>
              <IconButton size="small" onClick={loadList} disabled={listLoading}>
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreating(true);
                  setEditorOpen(true);
                }}
                sx={{ textTransform: 'none', bgcolor: COLORS.blue }}
              >
                New secret
              </Button>
            </Stack>

            <TableContainer
              sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
            >
              <Table size="small">
                <TableBody>
                  {listLoading && (
                    <TableRow>
                      <TableCell align="center">
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!listLoading &&
                    folders.map(entry => (
                      <TableRow
                        hover
                        key={entry}
                        onClick={() => onNavigate({ path: `${path}${entry}` })}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                          >
                            <FolderOutlinedIcon
                              sx={{ color: COLORS.amber, fontSize: 18 }}
                            />
                            <Typography sx={{ fontSize: 13 }}>
                              {entry}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  {!listLoading &&
                    leaves.map(entry => (
                      <TableRow
                        hover
                        key={entry}
                        onClick={() => openSecret(`${path}${entry}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                          >
                            <DescriptionOutlinedIcon
                              sx={{ color: COLORS.muted, fontSize: 18 }}
                            />
                            <Typography sx={{ fontSize: 13 }}>
                              {entry}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  {!listLoading && entries.length === 0 && (
                    <TableRow>
                      <TableCell align="center" sx={{ color: COLORS.muted }}>
                        No secrets at this path
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(secretPath)}
        onClose={closeSecret}
        PaperProps={{
          sx: { width: { xs: '100%', md: 640 }, maxWidth: '100vw' },
        }}
      >
        {secretPath && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <KeyIcon sx={{ mt: 0.5, color: COLORS.muted }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: COLORS.text,
                    fontSize: 17,
                    fontWeight: 700,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {secretPath}
                </Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                  {mount?.path} · {namespace || 'root'}
                </Typography>
              </Box>
              <IconButton onClick={closeSecret} aria-label="Close secret">
                <CloseIcon />
              </IconButton>
            </Box>

            <Stack direction="row" spacing={1} sx={{ my: 2 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<EditIcon />}
                disabled={busy || !secret?.data}
                onClick={() => {
                  setCreating(false);
                  setEditorOpen(true);
                }}
                sx={{ textTransform: 'none', bgcolor: COLORS.blue }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={busy}
                onClick={event => setDeleteAnchor(event.currentTarget)}
                sx={{ textTransform: 'none' }}
              >
                Delete
              </Button>
              <Menu
                anchorEl={deleteAnchor}
                open={Boolean(deleteAnchor)}
                onClose={() => setDeleteAnchor(null)}
              >
                <MenuItem onClick={() => runVersionOperation('delete')}>
                  Soft-delete latest version
                </MenuItem>
                {secret?.version && (
                  <MenuItem
                    onClick={() =>
                      setConfirmDestroy({
                        kind: 'destroy',
                        version: secret.version!,
                      })
                    }
                  >
                    Destroy version {secret.version}
                  </MenuItem>
                )}
                <MenuItem onClick={() => setConfirmDestroy({ kind: 'metadata' })}>
                  Permanently delete all versions
                </MenuItem>
              </Menu>
              {secret && (
                <TextField
                  select
                  size="small"
                  label="Version"
                  value={secret.version ?? ''}
                  onChange={event =>
                    onNavigate({ version: Number(event.target.value) })
                  }
                  sx={{ ml: 'auto', width: 130 }}
                >
                  {secret.versions.map(version => (
                    <MenuItem key={version.version} value={version.version}>
                      v{version.version}
                      {version.destroyed
                        ? ' · destroyed'
                        : version.deleted
                        ? ' · deleted'
                        : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>

            {secretLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={26} />
              </Box>
            )}
            {secretError && <Alert severity="error">{secretError}</Alert>}

            {secret && !secretLoading && (
              <>
                <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
                  {secret.deleted && (
                    <StatusChip label="Deleted" color={COLORS.red} />
                  )}
                  {secret.destroyed && (
                    <StatusChip label="Destroyed" color={COLORS.red} />
                  )}
                  {!secret.deleted && !secret.destroyed && (
                    <StatusChip label="Active" color={COLORS.green} />
                  )}
                </Stack>
                <Field label="Current version" value={secret.currentVersion} />
                <Field
                  label="Created"
                  value={
                    secret.createdTime
                      ? new Date(secret.createdTime).toLocaleString()
                      : undefined
                  }
                />

                {secret.data ? (
                  <TableContainer
                    sx={{
                      mt: 1.5,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Key</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell align="right" sx={{ width: 90 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(secret.data).map(([key, value]) => {
                          const text =
                            typeof value === 'string'
                              ? value
                              : JSON.stringify(value);
                          const shown = revealed.has(key);
                          return (
                            <TableRow key={key}>
                              <TableCell
                                sx={{ fontSize: 12.5, fontWeight: 600 }}
                              >
                                {key}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {shown ? text : '••••••••'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setRevealed(current => {
                                      const next = new Set(current);
                                      if (next.has(key)) next.delete(key);
                                      else next.add(key);
                                      return next;
                                    })
                                  }
                                >
                                  {shown ? (
                                    <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                  ) : (
                                    <VisibilityIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                                <Tooltip title="Copy value">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigator.clipboard.writeText(text)
                                    }
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    This version is {secret.destroyed ? 'destroyed' : 'deleted'}
                    — no data to show.
                  </Alert>
                )}

                <Typography
                  sx={{
                    mt: 2.5,
                    mb: 0.75,
                    color: COLORS.muted,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  VERSIONS
                </Typography>
                <TableContainer
                  sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
                >
                  <Table size="small">
                    <TableBody>
                      {secret.versions.map(version => (
                        <TableRow key={version.version}>
                          <TableCell sx={{ fontSize: 12 }}>
                            v{version.version}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {version.createdTime
                              ? new Date(version.createdTime).toLocaleString()
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {version.destroyed ? (
                              <StatusChip label="Destroyed" color={COLORS.red} />
                            ) : version.deleted ? (
                              <StatusChip label="Deleted" color={COLORS.amber} />
                            ) : (
                              <StatusChip label="Active" color={COLORS.green} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {version.deleted && !version.destroyed && (
                              <Tooltip title="Undelete version">
                                <IconButton
                                  size="small"
                                  disabled={busy}
                                  onClick={() =>
                                    runVersionOperation('undelete', [
                                      version.version,
                                    ])
                                  }
                                >
                                  <RestoreIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!version.destroyed && (
                              <Tooltip title="Destroy version (unrecoverable)">
                                <IconButton
                                  size="small"
                                  disabled={busy}
                                  onClick={() =>
                                    setConfirmDestroy({
                                      kind: 'destroy',
                                      version: version.version,
                                    })
                                  }
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </Drawer>

      <SecretEditor
        open={editorOpen}
        title={creating ? `New secret in ${path || '/'}` : `Edit ${secretPath}`}
        initialName={creating ? '' : secretPath ?? ''}
        nameEditable={creating}
        initialData={
          creating
            ? {}
            : Object.fromEntries(
                Object.entries(secret?.data ?? {}).map(([key, value]) => [
                  key,
                  typeof value === 'string' ? value : JSON.stringify(value),
                ]),
              )
        }
        busy={busy}
        onClose={() => setEditorOpen(false)}
        onSubmit={saveSecret}
      />

      <Dialog
        open={Boolean(confirmDestroy)}
        onClose={() => setConfirmDestroy(null)}
      >
        <DialogTitle>
          {confirmDestroy?.kind === 'metadata'
            ? 'Permanently delete secret?'
            : `Destroy version ${
                confirmDestroy?.kind === 'destroy'
                  ? confirmDestroy.version
                  : ''
              }?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDestroy?.kind === 'metadata'
              ? `All versions and metadata of ${secretPath} are removed. This cannot be undone.`
              : 'The version data is wiped and cannot be recovered.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDestroy(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={() =>
              confirmDestroy?.kind === 'metadata'
                ? deleteAllVersions()
                : confirmDestroy &&
                  runVersionOperation('destroy', [confirmDestroy.version])
            }
          >
            {confirmDestroy?.kind === 'metadata' ? 'Delete forever' : 'Destroy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
